import React, { useEffect, useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import ktx_logo_dark from "@/assets/ktx_logo_dark.png";
import ktx_logo_white from "@/assets/ktx_logo_white.png";
import { USER_ROLES } from "@/utils/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import useThemeStore from "@/utils/ThemeStore";
import { useNavigate } from "react-router-dom";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAppSelector } from "@/hooks";

export function TeamSwitcher({}: {
  teams: {
    name: string;
    text: string;
    plan: string;
    route: string;
  }[];
}) {
  const { open } = useSidebar();
  const user = useAppSelector((state) => state.user.user);
  const isAdmin = useIsAdmin();
  const teams = [
    ...(isAdmin
      ? [
          {
            name: "Kortex.ai",
            text: "User Dashboard",
            plan: "",
            route: "/dashboard/notes",
          },
        ]
      : [
          {
            name: "Kortex.ai",
            text: "Admin Dashboard",
            plan: "Admin",
            route: "/admin/dashboard/templates",
          },
        ]),
  ];

  const { theme } = useThemeStore();
  const { isMobile } = useSidebar();
  const [hovered, setHovered] = useState(false);
  const [activeTeam, setActiveTeam] = useState(
    {} as {
      name: string;
      text: string;
      plan: string;
      route: string;
    }
  );
  const navigate = useNavigate();

  useEffect(() => {
    const defaultPanel = isAdmin
      ? {
          name: "Kortex.ai",
          text: "Admin Dashboard",
          plan: "Admin",
          route: "/admin/dashboard/templates",
        }
      : {
          name: "Kortex.ai",
          text: "Kortex.ai",
          plan: "",
          route: "/dashboard/notes",
        };

    setActiveTeam(defaultPanel);
  }, []);

  if (!activeTeam) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={
                "active:bg-gray-25 dark:active:bg-gray-800 hover:bg-gray-25 dark:hover:bg-gray-800 " +
                (hovered
                  ? "hover:text-gray-800 hover:bg-gray-25 dark:hover:text-white dark:hover:bg-gray-500 "
                  : "")
              }
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              disabled={user.role === USER_ROLES.USER}
              style={
                user.role === USER_ROLES.USER
                  ? {
                      opacity: 1,
                    }
                  : {}
              }
            >
              <div className="flex aspect-square size-8 items-center justify-center">
                <div className="flex flex-col items-center justify-center">
                  <div className="h-[28px] w-[28px] flex items-center justify-center">
                    {hovered ? (
                      <img
                        src={theme === "dark" ? ktx_logo_white : ktx_logo_dark}
                        alt="Kortex"
                      />
                    ) : (
                      <img
                        src={theme === "dark" ? ktx_logo_white : ktx_logo_dark}
                        alt="Kortex"
                      />
                    )}
                  </div>
                  <div>
                    {isAdmin && !open && (
                      <div className="text-[10px]">Admin</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {activeTeam.name}
                </span>
                <span className="truncate text-xs">{activeTeam.plan}</span>
              </div>
              {teams.length && user.role && user.role !== USER_ROLES.USER && (
                <ChevronsUpDown className="ml-auto" />
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          {teams.length && (
            <DropdownMenuContent
              className="w-[220px] rounded-lg bg-white dark:bg-gray-800"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Switch to
              </DropdownMenuLabel>
              {teams.map((team, index) => (
                <DropdownMenuItem
                  key={team.name}
                  onClick={() => {
                    navigate(team.route);
                    setActiveTeam(team);
                  }}
                  className="gap-2 p-2 "
                >
                  {team.text}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          )}
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
